import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, Infinity as InfinityIcon, Sparkles, Wand2, Lightbulb, AtSign, X, ShieldCheck, Calendar, Users } from 'lucide-react';
import { api } from '../lib/api';
import { trackInitiateCheckout } from '../lib/analytics';
import { cn } from '../lib/cn';
import { PLATFORM_META, type Platform } from '../lib/platforms';
import { useAuth } from '../auth/AuthProvider';

const TRIAL_DAYS = 3;

type Plan = {
  id: string;
  name: string;
  description: string | null;
  priceMonthly: number;
  priceYearly: number;
  currency: string;
  unlimitedAccounts: boolean;
  aiPredictionsMonthly: number;
  aiCaptionsMonthly: number;
  aiIdeasMonthly: number;
  bioPagesLimit: number | null; // null = unlimited
  teamMembersLimit: number | null; // 0 = no teams, null = unlimited
  networks: { platform: string; maxAccounts: number }[];
};

type Period = 'monthly' | 'yearly';

const fmtMoney = (cents: number, currency: string) =>
  (cents / 100).toLocaleString('es-ES', { style: 'currency', currency });

export function PricingPlans({ currentPlanId }: { currentPlanId?: string | null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod]       = useState<Period>('monthly');
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError]         = useState<string | null>(null);
  const [confirmPlan, setConfirmPlan] = useState<Plan | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api<{ plans: Plan[] }>('/api/plans'),
  });
  const plans = data?.plans ?? [];

  async function subscribe(planId: string) {
    if (!user) { navigate('/register'); return; }
    setError(null);
    setLoadingId(planId);
    try {
      const { checkoutUrl, tracking } = await api<{
        checkoutUrl: string;
        tracking?: { eventId: string; value: number; currency: string; plan: string; period: Period };
      }>('/api/billing/checkout', {
        method: 'POST',
        body: JSON.stringify({ planId, period }),
      });
      if (tracking) {
        trackInitiateCheckout(tracking);
        // The pixels send their hits asynchronously; leaving the page at once
        // can cancel them. A short pause lets them go out (the server-side
        // Meta event is sent regardless).
        await new Promise((r) => setTimeout(r, 300));
      }
      window.location.href = checkoutUrl;
    } catch {
      setError('No se pudo iniciar el pago. Inténtalo de nuevo.');
      setLoadingId(null);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 text-slate-400 py-16">
        <Loader2 size={16} className="animate-spin" /> Cargando planes…
      </div>
    );
  }
  if (plans.length === 0) {
    return <p className="text-center text-slate-500 py-12">Pronto habrá planes disponibles.</p>;
  }

  return (
    <div className="space-y-7">
      {/* period toggle */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-xl bg-slate-100 p-1">
          {(['monthly', 'yearly'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={cn(
                'px-4 py-1.5 text-sm font-medium rounded-lg transition',
                period === p ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
              )}
            >
              {p === 'monthly' ? 'Mensual' : 'Anual'}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p className="max-w-md mx-auto text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2 text-center">
          {error}
        </p>
      )}

      {/* plan cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {plans.map((plan) => {
          const price = period === 'yearly' ? plan.priceYearly : plan.priceMonthly;
          const monthlyTotal = plan.priceMonthly * 12;
          const savingsPct = period === 'yearly' && monthlyTotal > 0 && plan.priceYearly < monthlyTotal
            ? Math.round(((monthlyTotal - plan.priceYearly) / monthlyTotal) * 100)
            : 0;
          const isCurrent = currentPlanId === plan.id;

          return (
            <div
              key={plan.id}
              className={cn(
                'bg-white rounded-2xl border p-6 flex flex-col gap-4 shadow-sm',
                isCurrent ? 'border-brand-400 ring-1 ring-brand-300' : 'border-slate-200',
              )}
            >
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-slate-900">{plan.name}</h3>
                  {isCurrent && (
                    <span className="text-[11px] font-medium rounded-full px-2 py-0.5 bg-brand-100 text-brand-700">
                      Tu plan
                    </span>
                  )}
                </div>
                {plan.description && (
                  <p className="mt-1 text-sm text-slate-500">{plan.description}</p>
                )}
              </div>

              {/* price */}
              <div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-extrabold text-slate-900">
                    {fmtMoney(price, plan.currency)}
                  </span>
                  <span className="text-sm text-slate-400">/{period === 'yearly' ? 'año' : 'mes'}</span>
                </div>
                {period === 'yearly' && (
                  <p className="mt-1 text-xs text-slate-500">
                    Equivale a {fmtMoney(Math.round(plan.priceYearly / 12), plan.currency)}/mes
                    {savingsPct > 0 && (
                      <span className="ml-1.5 text-emerald-600 font-semibold">· ahorras {savingsPct}%</span>
                    )}
                  </p>
                )}
              </div>

              {/* networks */}
              <div className="border-t border-slate-100 pt-3 flex-1">
                {plan.networks.length === 0 ? (
                  <p className="text-xs text-slate-400">Sin redes incluidas.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {plan.networks.map((n) => {
                      const meta = PLATFORM_META[n.platform as Platform];
                      if (!meta) return null;
                      const Icon = meta.icon;
                      return (
                        <li key={n.platform} className="flex items-center gap-2 text-sm text-slate-600">
                          <Icon size={15} color={meta.color} className="shrink-0" />
                          <span>{meta.label}</span>
                          <span className="ml-auto font-semibold text-slate-700 inline-flex items-center gap-0.5">
                            {plan.unlimitedAccounts
                              ? <InfinityIcon size={14} />
                              : <>{n.maxAccounts} {n.maxAccounts === 1 ? 'cuenta' : 'cuentas'}</>}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {plan.unlimitedAccounts && (
                  <p className="mt-2 text-xs text-brand-600 font-medium flex items-center gap-1">
                    <InfinityIcon size={12} /> Cuentas ilimitadas por red
                  </p>
                )}

                {/* Link in bio quota — only shown if the plan grants any. */}
                {(plan.bioPagesLimit === null || plan.bioPagesLimit > 0) && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <li className="flex items-center gap-2 text-sm text-slate-600 list-none">
                      <AtSign size={15} className="text-brand-500 shrink-0" />
                      <span>Link in bio</span>
                      <span className="ml-auto font-semibold text-slate-700 inline-flex items-center gap-0.5">
                        {plan.bioPagesLimit === null ? (
                          <InfinityIcon size={14} />
                        ) : (
                          <>
                            {plan.bioPagesLimit}{' '}
                            {plan.bioPagesLimit === 1 ? 'página' : 'páginas'}
                          </>
                        )}
                      </span>
                    </li>
                  </div>
                )}

                {/* Team seats — only shown when the plan grants teams. */}
                {(plan.teamMembersLimit === null || plan.teamMembersLimit > 0) && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <li className="flex items-center gap-2 text-sm text-slate-600 list-none">
                      <Users size={15} className="text-brand-500 shrink-0" />
                      <span>Equipos</span>
                      <span className="ml-auto font-semibold text-slate-700 inline-flex items-center gap-0.5">
                        {plan.teamMembersLimit === null ? (
                          <>
                            <InfinityIcon size={14} /> miembros
                          </>
                        ) : (
                          <>
                            {plan.teamMembersLimit}{' '}
                            {plan.teamMembersLimit === 1 ? 'miembro' : 'miembros'} extra
                          </>
                        )}
                      </span>
                    </li>
                  </div>
                )}
              </div>

              {/* AI quotas */}
              {(plan.aiPredictionsMonthly > 0 || plan.aiCaptionsMonthly > 0 || plan.aiIdeasMonthly > 0) && (
                <div className="border-t border-slate-100 pt-3">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">
                    Asistente IA
                  </div>
                  <ul className="space-y-1.5">
                    {plan.aiIdeasMonthly > 0 && (
                      <li className="flex items-center gap-2 text-sm text-slate-600">
                        <Lightbulb size={14} className="text-amber-500 shrink-0" />
                        <span>Ideas de contenido</span>
                        <span className="ml-auto font-semibold text-slate-700 tabular-nums">
                          {plan.aiIdeasMonthly}/mes
                        </span>
                      </li>
                    )}
                    {plan.aiCaptionsMonthly > 0 && (
                      <li className="flex items-center gap-2 text-sm text-slate-600">
                        <Wand2 size={14} className="text-accent-500 shrink-0" />
                        <span>Captions con IA</span>
                        <span className="ml-auto font-semibold text-slate-700 tabular-nums">
                          {plan.aiCaptionsMonthly}/mes
                        </span>
                      </li>
                    )}
                    {plan.aiPredictionsMonthly > 0 && (
                      <li className="flex items-center gap-2 text-sm text-slate-600">
                        <Sparkles size={14} className="text-brand-500 shrink-0" />
                        <span>Predicciones de rendimiento</span>
                        <span className="ml-auto font-semibold text-slate-700 tabular-nums">
                          {plan.aiPredictionsMonthly}/mes
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* cta */}
              <button
                onClick={() => {
                  if (!user) { navigate('/register'); return; }
                  setConfirmPlan(plan);
                }}
                disabled={loadingId !== null || isCurrent}
                className="btn-primary w-full gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingId === plan.id && <Loader2 size={14} className="animate-spin" />}
                {isCurrent ? 'Plan actual' : loadingId === plan.id ? 'Redirigiendo…' : 'Suscribirme'}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
        <Check size={13} className="text-emerald-500" /> 3 días de prueba gratis · cancela cuando quieras
      </p>

      {confirmPlan && (
        <TrialConfirmModal
          plan={confirmPlan}
          period={period}
          loading={loadingId === confirmPlan.id}
          onClose={() => setConfirmPlan(null)}
          onConfirm={() => {
            const id = confirmPlan.id;
            setConfirmPlan(null);
            subscribe(id);
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Trial confirmation modal — shown before redirecting the
   user to Mollie so they understand the €0 charge is just
   for activating the 3-day free trial.
   ───────────────────────────────────────────────────────── */
function TrialConfirmModal({
  plan, period, loading, onClose, onConfirm,
}: {
  plan: Plan;
  period: Period;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cents = period === 'yearly' ? plan.priceYearly : plan.priceMonthly;
  const price = fmtMoney(cents, plan.currency);
  const cadence = period === 'yearly' ? 'año' : 'mes';
  const firstChargeDate = new Date(Date.now() + TRIAL_DAYS * 86_400_000)
    .toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>

          <div className="h-12 w-12 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600 mb-4">
            <ShieldCheck size={22} />
          </div>
          <h3 className="text-xl font-bold text-slate-900">
            Empieza tu prueba gratis de Zettapost {plan.name}
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            Te pediremos los datos de pago para validar la tarjeta, pero
            <strong> no te cobraremos {price}</strong> hasta que terminen
            los {TRIAL_DAYS} días.
          </p>

          <div className="mt-5 space-y-3 rounded-xl bg-slate-50 border border-slate-200 p-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <Check size={14} />
              </span>
              <div className="text-sm">
                <p className="font-semibold text-slate-900">Hoy: 0,00 €</p>
                <p className="text-slate-500">
                  Activamos tu cuenta y validamos tu método de pago.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center">
                <Calendar size={14} />
              </span>
              <div className="text-sm">
                <p className="font-semibold text-slate-900">
                  {firstChargeDate}: {price}
                </p>
                <p className="text-slate-500">
                  Primera cuota recurrente ({price}/{cadence}). Puedes
                  cancelar antes de esta fecha sin coste.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn-secondary flex-1 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={loading}
              className="btn-primary flex-[2] gap-2 disabled:opacity-50"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? 'Redirigiendo…' : 'Continuar al pago'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
